const API_BASE = import.meta.env.VITE_API_URL || '/api';

/** Admin image URLs are relative to the API's origin (/api/admin/...). */
export function resolveImageUrl(path?: string | null): string | undefined {
  if (!path) return undefined;
  if (path.startsWith('http')) return path;
  return `${API_BASE.replace(/\/api$/, '')}${path}`;
}

export async function validateLogin(username: string, password: string): Promise<boolean> {
  const response = await fetch(`${API_BASE}/admin/cars/summary`, {
    headers: { Authorization: `Basic ${btoa(`${username}:${password}`)}` },
  });
  return response.ok;
}

export function setAuth(username: string, password: string): void {
  localStorage.setItem('admin_auth', btoa(`${username}:${password}`));
}

export function clearAuth(): void {
  localStorage.removeItem('admin_auth');
}

export function isAuthenticated(): boolean {
  return !!localStorage.getItem('admin_auth');
}

export interface AdminCar {
  id: string;
  name: string;
  description: string | null;
  year: number | null;
  series: string | null;
  color: string | null;
  toyNumber: string | null;
  barcode: string | null;
  tags: string[];
  imageUrl: string | null;
  moderationStatus: string;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  createdByUserId?: string | null;
  sourceSubmissionId?: string | null;
  aiConfidence?: number | null;
}

export interface AdminCarSubmission {
  id: string;
  status: string;
  carId: string | null;
  barcode: string | null;
  toyNumber: string | null;
  name: string | null;
  year: number | null;
  series: string | null;
  color: string | null;
  notes: string | null;
  ai: { validationResult: string; confidence: number | null; isCar: boolean | null; containsPeople: boolean | null; reasoning: string | null } | null;
  aiAnalysis: { analysis?: Record<string, unknown> | null; error?: string; validationResult?: string } | null;
  rejectReason: string | null;
  reviewedAt: string | null;
  createdAt: string;
  userId: string | null;
  imageUrl: string | null;
  car: AdminCar | null;
  isSourceOfCar: boolean;
}

export interface CarSettings {
  aiApprovedRequiresReview: boolean;
  submissionsEnabled: boolean;
  submissionsPerUserPerDay: number;
  updatedAt: string | null;
}

interface ApiOptions {
  method?: string;
  body?: unknown;
}

type Paged<K extends string, T> = { data: { [key in K]: T[] } & { pagination: { total: number; page: number; limit: number; pages: number } } };

// Routes return { status: 'error', code, message } on failure.
async function fetchCars<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const auth = localStorage.getItem('admin_auth');
  const response = await fetch(`${API_BASE}/admin/cars${endpoint}`, {
    method: options.method ?? 'GET',
    headers: {
      ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(auth ? { Authorization: `Basic ${auth}` } : {}),
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });
  if (response.status === 401) {
    localStorage.removeItem('admin_auth');
    window.location.reload();
    throw new Error('Unauthorized');
  }
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'API request failed');
  return data;
}

function query(params: Record<string, string | number | undefined>): string {
  const q = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join('&');
  return q ? `?${q}` : '';
}

export async function getCarsSummary() {
  return fetchCars<{ data: {
    cars: { total: number; pending: number; aiApproved: number; approved: number; rejected: number };
    submissions: { total: number; pending: number; aiApproved: number; duplicate: number };
    collections: { owned: number; wishlist: number };
  } }>('/summary');
}

export async function getCarSubmissions(page = 1, limit = 20, status?: string, search?: string) {
  return fetchCars<Paged<'submissions', AdminCarSubmission>>(`/submissions${query({ page, limit, status, q: search })}`);
}

export async function reviewCarSubmission(id: string, body: { status: string; rejectReason?: string; car?: Record<string, unknown> }) {
  return fetchCars<{ data: { submission: AdminCarSubmission } }>(`/submissions/${id}`, { method: 'PATCH', body });
}

export async function getCarItems(page = 1, limit = 20, status?: string, search?: string) {
  return fetchCars<Paged<'cars', AdminCar>>(`/items${query({ page, limit, status, q: search })}`);
}

export async function updateCarItem(id: string, body: Record<string, unknown>) {
  return fetchCars<{ data: { car: AdminCar } }>(`/items/${id}`, { method: 'PATCH', body });
}

export async function deleteCarItem(id: string) {
  return fetchCars<{ data: { deleted: boolean; removedFromCollections: number } }>(`/items/${id}`, { method: 'DELETE' });
}

export async function replaceCarImage(id: string, file: File) {
  const auth = localStorage.getItem('admin_auth');
  const form = new FormData();
  form.set('image', file);
  const response = await fetch(`${API_BASE}/admin/cars/items/${id}/image`, {
    method: 'PUT',
    headers: auth ? { Authorization: `Basic ${auth}` } : {},
    body: form,
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Upload failed');
  return data as { data: { car: AdminCar } };
}

export async function getCarSettings() {
  return fetchCars<{ data: { settings: CarSettings } }>('/settings');
}

export async function updateCarSettings(settings: Partial<CarSettings>) {
  return fetchCars<{ data: { settings: CarSettings } }>('/settings', { method: 'PUT', body: settings });
}

/**
 * Admin images need the Basic auth header (they include photos still in review),
 * so they can't be a plain <img src>. Returns an object URL.
 */
export async function fetchAuthedImage(url: string): Promise<string> {
  const auth = localStorage.getItem('admin_auth');
  const response = await fetch(url, { headers: auth ? { Authorization: `Basic ${auth}` } : {} });
  if (!response.ok) throw new Error('Image not found');
  return URL.createObjectURL(await response.blob());
}
